import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, type FileItem } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API_BASE_URL, API_PREFIX, MAX_FILE_SIZE, CHUNK_SIZE } from '../config';
import { useChunkedUpload } from './useChunkedUpload';

export function useFiles() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const filesQuery = useQuery<FileItem[]>({
    queryKey: ['files'],
    queryFn: () => apiClient.listFiles(token),
    enabled: Boolean(token),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteFile(token, id),
    onSuccess: () => {
      toast.success('文件已删除');
      queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : '删除失败');
    },
  });

  const chunkedUpload = useChunkedUpload();

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`文件超过 ${Math.round(MAX_FILE_SIZE / 1024 / 1024 / 1024)}GB 限制`);
      }

      // 小文件使用简单上传，大文件使用分片上传
      if (file.size <= CHUNK_SIZE * 10) { // 小于 5MB 使用简单上传
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_BASE_URL}${API_PREFIX}/upload/simple`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });

        if (!response.ok) {
          const errorMessage = await response.text();
          throw new Error(errorMessage || '简单上传失败');
        }

        return response.json();
      } else {
        // 大文件使用分片上传
        await chunkedUpload.uploadFile(file);
        return null; // 分片上传成功后通过 queryClient.invalidateQueries 刷新列表
      }
    },
    onSuccess: () => {
      toast.success('上传成功');
      queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (error: unknown) => {
      console.error('上传失败:', error);
      toast.error(error instanceof Error ? error.message : '上传失败');
    },
  });

  return {
    filesQuery,
    deleteMutation,
    uploadMutation,
    chunkedUpload,
  };
}